// Notification mode for conversation settings
export type NotificationMode = 'Default' | 'Muted' | 'NoticeOnly' | 'AutoOpen'

// Map numeric enum values from API to string NotificationMode. Must match the
// backend ConversationNotificationMode enum exactly (serialized as int):
// Default=0, Muted=1, NoticeOnly=2, AutoOpen=3.
const NOTIFICATION_MODE_MAP: Record<number, NotificationMode> = {
  0: 'Default',
  1: 'Muted',
  2: 'NoticeOnly',
  3: 'AutoOpen',
}

export function resolveNotificationMode(mode: NotificationMode | number | undefined | null): NotificationMode {
  if (mode == null || mode === 'Default') return 'NoticeOnly'
  if (typeof mode === 'number') return NOTIFICATION_MODE_MAP[mode] ?? 'NoticeOnly'
  return mode
}

const NOTIFICATION_MODE_TO_INT: Record<NotificationMode, number> = {
  Default: 0,
  Muted: 1,
  NoticeOnly: 2,
  AutoOpen: 3,
}

export function notificationModeToInt(mode: NotificationMode): number {
  return NOTIFICATION_MODE_TO_INT[mode] ?? 2
}

// Chat conversation types
export enum ConversationType {
  Direct = 0,
  Group = 1,
  Adventure = 2,
  /** The people editing one adventure, inside its editor. Never in the Messages inbox. */
  AdventureEditor = 3,
}

// Participant role in a conversation
export type ParticipantRole = 'Member' | 'Admin' | 'Owner'

// Participant in a conversation
export interface ConversationParticipant {
  id: string
  userId: string
  displayName: string
  role: ParticipantRole
  joinedAt: string
  lastReadAt: string | null
  isActive: boolean
  isOnline: boolean
  notificationMode?: NotificationMode | number
  avatarUrl?: string | null
}

// Conversation settings request/response
export interface ConversationSettings {
  notificationMode: NotificationMode
}

// Conversation DTO
export interface ConversationDto {
  id: string
  name: string | null
  type: ConversationType
  createdAt: string
  lastMessageAt: string | null
  lastMessageContent: string | null
  isActive: boolean
  unreadMessageCount: number
  notificationMode?: NotificationMode | number
  messageCount: number
  participants: ConversationParticipant[]
  // For adventure chats
  adventureId?: string | null
  adventureAvatar?: string | null
  // For group chats
  groupId?: string | null
  groupAvatar?: string | null
  // When set, the chat will be archived (hidden from users) at this time.
  // Populated when the linked adventure session ends — clients show a live
  // countdown banner.
  expiresAt?: string | null
}

// Read receipt entry
export interface MessageReadBy {
  userId: string
  displayName: string
  readAt: string
}

// Reaction entry
export interface MessageReaction {
  userId: string
  displayName: string
  emoji: string
}

// A member who opened an ephemeral image (sender's in-chat "opened by" receipt)
export interface ImageViewer {
  userId: string
  displayName: string
  viewsRemaining: number
}

// Chat message DTO
export interface ChatMessageDto {
  id: string
  conversationId: string
  senderId: string
  senderName: string
  senderDisplayName?: string
  senderAvatarUrl: string | null
  content: string
  imageUrl?: string | null
  timestamp: string
  isRead: boolean
  isDeleted: boolean
  isEdited: boolean
  isOwnMessage?: boolean
  isSystemMessage?: boolean
  metadata?: string | null
  /** What this message points at, as the requesting reader may see it. */
  references?: ChatReferenceDto[]
  editedAt: string | null
  // Ephemeral (view-limited) image state for the current viewer. imageMaxViews null/undefined = normal
  // image. A limited image NEVER has an imageUrl (not even for its sender): viewImage() returns a
  // one-time token and the bytes come from fetchImageByToken().
  imageMaxViews?: number | null
  imageViewsRemaining?: number | null
  isImageArchived?: boolean
  // Max seconds the image stays open per reveal before auto-closing (null/undefined = no time limit).
  imageViewSeconds?: number | null
  // Sender's in-chat receipt: who has opened this ephemeral image + their remaining views.
  imageViewers?: ImageViewer[]
  readBy?: MessageReadBy[]
  reactions?: MessageReaction[]
}

// Paginated messages response
export interface MessagesResponse {
  items: ChatMessageDto[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasMore: boolean
}

// Request to send a message
/** 0 = Adventure, 1 = Group. Matches the server's ChatReferenceKind. */
export const ChatReferenceKind = {
  Adventure: 0,
  Group: 1,
} as const

/** What the sender attaches: a kind and an id, never a rendered card. */
export interface ChatReferenceInput {
  kind: number
  id: string
}

/**
 * A reference as *this* reader is allowed to see it.
 *
 * When `visible` is false the descriptive fields are absent — the server never loads them, so
 * there is nothing here to leak. Do not fill the gap from a local cache: two people in one
 * conversation can be owed different answers, and that is the feature rather than an edge case.
 */
export interface ChatReferenceDto {
  kind: number
  id: string
  visible: boolean
  title?: string | null
  description?: string | null
  imageUrl?: string | null
  /** 'private' or 'gone' when not visible; absent when it is. */
  unavailable?: string | null
}

export interface SendMessageRequest {
  content: string
  imageId?: string
  // Per-viewer view cap for an ephemeral image (omit for a normal image).
  imageMaxViews?: number
  // Max seconds the image stays open per reveal (omit for no time limit).
  imageViewSeconds?: number
  /** Adventures or groups this message points at. Ids only — visibility is per reader. */
  references?: ChatReferenceInput[]
}

// Response from spending a view on an ephemeral image
export interface ImageViewResponse {
  /** Ordinary images only. A view-limited image never has a URL. */
  imageUrl?: string | null
  /**
   * View-limited images only: a one-time token for GET /api/Chat/images/{token}, good for one load
   * by this user for about a minute. Fetch the bytes with the auth header and show them from memory.
   */
  viewToken?: string | null
  viewTokenExpiresAt?: string | null
  viewsRemaining: number | null
}

// Response from image upload
export interface ChatImageUploadResponse {
  imageId: string
  imageUrl: string
  width: number
  height: number
  fileSize: number
}

// Request to create a direct conversation
export interface CreateDirectConversationRequest {
  recipientId: string
}

// Request to create a group conversation
export interface CreateGroupConversationRequest {
  name: string
  participantIds: string[]
}

// User search result for chat
export interface ChatUserSearchResult {
  id: string
  userName: string
  displayName: string
  avatarUrl: string | null
  isOnline: boolean
}

// Chat notification
export interface ChatNotification {
  id: string
  conversationId: string
  conversationName: string | null
  conversationType: ConversationType
  senderId: string
  senderName: string
  senderAvatarUrl: string | null
  messagePreview: string
  timestamp: string
  isRead: boolean
}

// Online friend
export interface OnlineFriend {
  id: string
  userName: string
  displayName: string
  avatarUrl: string | null
  lastSeenAt: string
}

// Typing indicator
export interface TypingIndicator {
  conversationId: string
  userId: string
  userName: string
  isTyping: boolean
}
